
  import Vue from "vue"
  import axios, { AxiosError, AxiosResponse } from "axios"
  import { EventBus } from "@/plugins/eventBus"
  
  interface Client {
    id: number
    name: string
    positions?: Position[]
  }

  interface Position {
    id: number
    name: string
    priority: number | null
    totalDelegated: number
    campaign_count: number
    answer_count_yes: number
    answer_count_no: number
  }

  // Extended interface for positions with client name
  interface PositionWithClient extends Position {
    clientName: string
  }

  interface Response {
    clients?: {
      [key: string]: Client | Position[];
      unmatch_positions: Position[];
      ban_positions: Position[];
    } | Client[];
    favorite_jobs?: string[];
  }

  // Define the interface for the table headers
  interface DataTableHeader {
    text: string
    value: string
    align?: 'start' | 'center' | 'end'
    sortable?: boolean
    filterable?: boolean
    groupable?: boolean
    divider?: boolean
    class?: string | string[]
    cellClass?: string | string[]
    width?: string | number
  }

  export default Vue.extend({
    name: 'WorkerPositions',
    props: {
      id: {
        type: Number,
        required: true,
        default: 1
      },
    },
    data() {
      return {
        selected: [] as PositionWithClient[],
        currentPageItems: [] as PositionWithClient[],
        clients: [] as Client[],
        favorite_jobs: [] as string[],
        action: 'dissociate',
        actions: [
          { text: this.$vuetify.lang.t('$vuetify.remove_positions'), value: 'dissociate' }
        ],
        associatedPositionsSearch: '',
        selectedPage: false,
        pageIndeterminate: false,
        allSelected: false,
        allIndeterminate: false,
        tableHeaders: [
            { 
              text: this.$vuetify.lang.t('$vuetify.position') + ' / ' + this.$vuetify.lang.t('$vuetify.client'), 
              value: 'name',
              width: '110px',
              align: 'start'
            },
            { 
              text: this.$vuetify.lang.t('$vuetify.priority'), 
              value: 'priority',
              align: 'center',
              class: 'centered-cell',
              width: '100px'
            },
            { 
              text: this.$vuetify.lang.t('$vuetify.total_delegated'), 
              value: 'totalDelegated',
              align: 'center',
              class: 'centered-cell',
              width: '101px'
            },
            { 
              text: this.$vuetify.lang.t('$vuetify.campaign_count'), 
              value: 'campaign_count',
              align: 'center',
              class: 'centered-cell',
              width: '108px'
            },
            { 
              text: this.$vuetify.lang.t('$vuetify.nb_yes'), 
              value: 'answer_count_yes',
              align: 'center',
              class: 'centered-cell',
              width: '108px'
            },
            { 
              text: this.$vuetify.lang.t('$vuetify.nb_no'), 
              value: 'answer_count_no',
              align: 'center',
              class: 'centered-cell',
              width: '108px'
            },
            { 
              text: 'Actions', 
              value: 'actions',
              sortable: false,
              align: 'center'
            }
          ] as DataTableHeader[],
        calculatedPositions: [] as PositionWithClient[]
      }
    },
    mounted() {
      this.getData()
    },
    methods: {
      getData() {
        axios
          .get(`/v1/workers/${this.id}?fields=favorite_jobs,clients`)
          .then((response: AxiosResponse<Response>) => {
            if (response.data.clients) {
              if (Array.isArray(response.data.clients)) {
                this.clients = response.data.clients;
              } else {
                const clientsObj = response.data.clients;
                const { unmatch_positions, ban_positions, ...clientsData } = clientsObj;
                this.clients = Object.values(clientsData)
                  .filter(client => typeof client === 'object' && client !== null) as Client[];
              }
            }
            
            this.favorite_jobs = Array.isArray(response.data.favorite_jobs) 
              ? response.data.favorite_jobs 
              : [];
            
            this.calculatePositionsTable();
          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar',{axiosError: error})
          })
      },
      calculatePositionsTable() {
        if (!Array.isArray(this.clients) || this.clients.length === 0) {
          this.calculatedPositions = [];
          return;
        }
        
        const result: PositionWithClient[] = [];
        
        for (const client of this.clients) {
          if (!client || typeof client !== 'object') {
            continue;
          }
          
          const clientName = client.name || 'Client sans nom';
          
          if (!client.positions || !Array.isArray(client.positions)) {
            continue;
          }
          
          for (const position of client.positions) {
            if (!position || typeof position !== 'object') {
              continue;
            }
            
            result.push({
              ...position,
              clientName: clientName
            });
          }
        }
        
        this.calculatedPositions = result;
      },
      dissociate() {
        const payload = {
          [this.id]: {
            remove_positions: this.selected.map(item => item.id)
          }
        }

        axios
          .put('/v1/workers', payload)
          .then(() => {
            // Reset selections
            this.selected = [];
            this.selectedPage = false;
            this.pageIndeterminate = false;
            this.allSelected = false;
            this.allIndeterminate = false;
            
            // Show success message
            EventBus.$emit('snackbar', {
              message: this.$vuetify.lang.t('$vuetify.dissociation_success'),
              color: 'success'
            });
            
            // Reload data
            this.getData();
          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar', { axiosError: error })
          })
      },
      updateCurrentItems(items: PositionWithClient[]) {
        this.currentPageItems = items;
        this.updateSelectionState();
      },
      selectPage() {
        if (this.selectedPage) {
          this.selected = this.selected.filter(item => 
            !this.currentPageItems.some(pageItem => pageItem.id === item.id)
          );
          this.selectedPage = false;
          this.pageIndeterminate = false;
        } else {
          const newSelected = [...this.selected];
          this.currentPageItems.forEach(item => {
            if (!newSelected.some(selected => selected.id === item.id)) {
              newSelected.push(item);
            }
          });
          this.selected = newSelected;
          this.selectedPage = true;
          this.pageIndeterminate = false;
        }
        this.updateSelectionState();
      },
      selectAllFilteredItems() {
        if (this.allSelected) {
          this.selected = [];
          this.allSelected = false;
          this.allIndeterminate = false;
        } else {
          this.selected = [...this.calculatedPositions];
          this.allSelected = true;
          this.allIndeterminate = false;
        }
        this.updateSelectionState();
      },
      updateSelectionState() {
        const pageSelectedCount = this.currentPageItems.filter(item =>
          this.selected.some(selectedItem => selectedItem.id === item.id)
        ).length;

        if (pageSelectedCount === 0) {
          this.selectedPage = false;
          this.pageIndeterminate = false;
        } else if (pageSelectedCount === this.currentPageItems.length) {
          this.selectedPage = true;
          this.pageIndeterminate = false;
        } else {
          this.selectedPage = false;
          this.pageIndeterminate = true;
        }

        const totalSelectedCount = this.selected.length;

        if (totalSelectedCount === 0) {
          this.allSelected = false;
          this.allIndeterminate = false;
        } else if (totalSelectedCount === this.calculatedPositions.length) {
          this.allSelected = true;
          this.allIndeterminate = false;
        } else {
          this.allSelected = false;
          this.allIndeterminate = true;
        }
      },
      dissociateOne(item: PositionWithClient) {
        const payload = {
          [this.id]: {
            remove_positions: [item.id]
          }
        }

        axios
          .put('/v1/workers', payload)
          .then(() => {
            // Remove the item from the selection if it was in it
            this.selected = this.selected.filter(selected => selected.id !== item.id);

            // Show success message
            EventBus.$emit('snackbar', {
              message: this.$vuetify.lang.t('$vuetify.dissociation_success'),
              color: 'success'
            });
            
            // Update the state of the checkboxes
            this.updateSelectionState();
            
            // Reload the data
            this.getData();
          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar', { axiosError: error })
          })
      }
    },
    computed: {
      headers(): DataTableHeader[] {
        return this.tableHeaders;
      },
      positionsTable(): PositionWithClient[] {
        return this.calculatedPositions;
      },
      isPageFullySelected(): boolean {
        return this.currentPageItems.every(item => 
          this.selected.some(selectedItem => selectedItem.id === item.id)
        );
      },
      isPagePartiallySelected(): boolean {
        const selectedInPage = this.currentPageItems.some(item => 
          this.selected.some(selectedItem => selectedItem.id === item.id)
        );
        return selectedInPage && !this.isPageFullySelected;
      },
      isAllPartiallySelected(): boolean {
        const someSelected = this.calculatedPositions.some(item => 
          this.selected.some(selectedItem => selectedItem.id === item.id)
        );
        return someSelected && !this.allSelected;
      },
      isAllFullySelected(): boolean {
        return this.calculatedPositions.every(item => 
          this.selected.some(selectedItem => selectedItem.id === item.id)
        );
      }
    },
    watch: {
      clients: {
        handler(newClients) {
          if (Array.isArray(newClients)) {
            this.calculatePositionsTable();
          }
        },
        deep: true
      },
      id: {
        handler() {
          // Reset the data
          this.clients = [];
          this.favorite_jobs = [];
          this.calculatedPositions = [];
          
          // Reset the selection
          this.selected = [];
          this.selectedPage = false;
          this.pageIndeterminate = false;
          this.allSelected = false;
          this.allIndeterminate = false;
          
          // Get the new data
          this.getData();
        },
        immediate: true
      },
      selected: {
        handler() {
          this.updateSelectionState();
        },
        deep: true
      }
    }
  })
